// CharacterCreator: hand-make a character, styled like the bio dossier.
// Name inputs, sliders for all 11 nature stats, up to 5 learned traits
// (trait picker + strength + reason capped at 100 chars), palette pickers.
// Result is origin 'human' — neither LLM nor offline-made.
import { useState } from 'react';
import { TRAIT_SEEDS } from '../db/traits.js';
import { uid } from './spawn.js';
import { NATURE_LABELS } from './BioPanel.js';
import { displayLabel, traitOptionLabel } from './displayLabel.js';
import type { Nature, Npc, NurtureTrait } from '../types.js';

const MAX_TRAITS = 5;
const MAX_REASON = 100;

export interface DraftTrait {
  trait_key: string;
  strength: number;
  reason: string;
}

const blankNature = (): Nature => ({
  sociability: 5, agreeableness: 5, emotionality: 5, caution: 5,
  discipline: 5, dominance: 5, empathy: 5, curiosity: 5, drive: 5,
  sexual_drive: 5, casual_sex_openness: 5,
});

const blankTrait = (): DraftTrait => ({ trait_key: '', strength: 5, reason: '' });

export function validateDraft(
  first: string,
  last: string,
  traits: DraftTrait[],
  options?: { privacyNames?: boolean },
): string | null {
  if (!first.trim()) {
    return options?.privacyNames ? 'First name is required.' : 'Give your character a first and last name.';
  }
  if (!options?.privacyNames && !last.trim()) return 'Give your character a first and last name.';
  if (options?.privacyNames && last.trim().length > 1) return 'Last initial is one character max.';
  const filled = traits.filter((t) => t.trait_key || t.reason.trim());
  if (filled.length === 0) return 'Add at least one learned trait.';
  const seen = new Set<string>();
  for (const t of filled) {
    if (!t.trait_key) return 'Every trait needs a trait picked from the list.';
    if (!t.reason.trim()) return 'Every trait needs a reason (what happened).';
    if (t.reason.trim().length > MAX_REASON) return `Reasons cap at ${MAX_REASON} characters.`;
    if (seen.has(t.trait_key)) return 'No duplicate traits — pick distinct ones.';
    seen.add(t.trait_key);
  }
  return null;
}

export function buildCustomNpc(
  first: string,
  last: string,
  nature: Nature,
  traits: DraftTrait[],
  palette: Npc['palette'],
): Npc {
  const nurture: NurtureTrait[] = traits
    .filter((t) => t.trait_key && t.reason.trim())
    .map((t) => ({ trait_key: t.trait_key, strength: t.strength, reason: t.reason.trim() }));
  return {
    id: uid('npc'),
    firstName: first.trim().slice(0, 40),
    lastName: last.trim().slice(0, 40),
    palette: { ...palette },
    nature: { ...nature },
    nurture,
    state: { mood: 'neutral', stress: 3, energy: 6 },
    savedFavorite: true,
    origin: 'human',
    neurotic: false,
  };
}

export function npcToPrivacyDraftMeta(
  npc: Npc,
  context: { species: string; sex: 'M' | 'F'; ageRange: string },
): Omit<import('../domain/types.js').CharacterDraftMeta, 'draftCharacterId' | 'schemaVersion' | 'updatedAt'> {
  const initial = npc.lastName.endsWith('.')
    ? npc.lastName.slice(0, -1).charAt(0)
    : npc.lastName.charAt(0);
  return {
    firstName: npc.firstName,
    lastInitial: initial || undefined,
    species: context.species,
    sex: context.sex,
    ageRange: context.ageRange,
    status: 'draft',
    source: 'user',
  };
}

export default function CharacterCreator(props: {
  onSave: (npc: Npc) => void;
  onClose: () => void;
  /** Privacy mode: first name + optional last initial (Create World handoff). */
  nameMode?: 'full' | 'privacy';
}): JSX.Element {
  const privacyNames = props.nameMode === 'privacy';
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [nature, setNature] = useState<Nature>(blankNature);
  const [traits, setTraits] = useState<DraftTrait[]>([blankTrait()]);
  const [palette, setPalette] = useState({ skin: '#f1c27d', shirt: '#4cc9f0', pants: '#3a506b', hair: '#3b2f2f' });
  const [error, setError] = useState<string | null>(null);

  const setTrait = (i: number, patch: Partial<DraftTrait>): void => {
    setTraits((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  };

  function save(): void {
    const err = validateDraft(first, last, traits, { privacyNames });
    if (err) {
      setError(err);
      return;
    }
    const lastForNpc = privacyNames
      ? (last.trim() ? `${last.trim().charAt(0)}.` : '—')
      : last;
    props.onSave(buildCustomNpc(first, lastForNpc, nature, traits, palette));
  }

  return (
    <div className="modal-back" onClick={props.onClose}>
      <div className="bio creator" onClick={(e) => e.stopPropagation()}>
        <div className="bio-head">
          <div>
            <h3>New Character ◈</h3>
            <div className="bio-sub">Hand-made by you — human-made, not generated.</div>
          </div>
          <button className="bio-close" onClick={props.onClose} aria-label="Close creator">✕</button>
        </div>
        <div className="bio-body">
          <strong>Name</strong>
          <div className="creator-names">
            <input value={first} maxLength={40} placeholder="First" onChange={(e) => setFirst(e.target.value)} aria-label="First name" />
            <input
              value={last}
              maxLength={privacyNames ? 1 : 40}
              placeholder={privacyNames ? 'Initial (optional)' : 'Last'}
              onChange={(e) => setLast(privacyNames ? e.target.value.slice(0, 1) : e.target.value)}
              aria-label={privacyNames ? 'Last initial' : 'Last name'}
            />
          </div>
          <strong>Look</strong>
          <div className="creator-palette">
            {(['skin', 'shirt', 'pants', 'hair'] as const).map((part) => (
              <label key={part}>
                {part}
                <input type="color" value={palette[part]} onChange={(e) => setPalette((p) => ({ ...p, [part]: e.target.value }))} aria-label={`${part} color`} />
              </label>
            ))}
          </div>
          <strong>Nature</strong>
          {NATURE_LABELS.map(([k, low, high]) => (
            <div key={k} className="creator-slider">
              <div className="rel-label">
                <span>{displayLabel(k)}</span>
                <span className="mut">{nature[k]}/10 · {nature[k] <= 4 ? low : nature[k] >= 7 ? high : 'balanced'}</span>
              </div>
              <input
                type="range" min={1} max={10} step={1} value={nature[k]}
                onChange={(e) => setNature((n) => ({ ...n, [k]: Number(e.target.value) }))}
                aria-label={displayLabel(k)}
              />
            </div>
          ))}
          <strong>Learned traits ({traits.filter((t) => t.trait_key || t.reason).length}/{MAX_TRAITS})</strong>
          {traits.map((t, i) => (
            <div key={i} className="trait creator-trait">
              <select value={t.trait_key} onChange={(e) => setTrait(i, { trait_key: e.target.value })} aria-label={`Trait ${i + 1}`}>
                <option value="">— pick a trait —</option>
                {TRAIT_SEEDS.map(([key, cat]) => (
                  <option key={key} value={key}>{traitOptionLabel(key, cat)}</option>
                ))}
              </select>
              <div className="rel-label">
                <span>strength</span>
                <span className="mut">{t.strength}/10</span>
              </div>
              <input
                type="range" min={1} max={10} step={1} value={t.strength}
                onChange={(e) => setTrait(i, { strength: Number(e.target.value) })}
                aria-label={`Trait ${i + 1} strength`}
              />
              <textarea
                value={t.reason}
                maxLength={MAX_REASON}
                rows={2}
                placeholder="What happened? One line (100 chars max)."
                onChange={(e) => setTrait(i, { reason: e.target.value })}
                aria-label={`Trait ${i + 1} reason`}
              />
              <div className="rel-hint">{t.reason.length}/{MAX_REASON}</div>
              {traits.length > 1 && (
                <button className="btn-ghost" onClick={() => setTraits((ts) => ts.filter((_, j) => j !== i))}>Remove</button>
              )}
            </div>
          ))}
          {traits.length < MAX_TRAITS && (
            <button className="btn-ghost" onClick={() => setTraits((ts) => [...ts, blankTrait()])}>+ Add trait</button>
          )}
          {error && <div className="creator-error">{error}</div>}
          <div className="menu-row">
            <button className="btn-ghost" onClick={props.onClose}>Cancel</button>
            <button className="btn-primary" onClick={save}>Save Character</button>
          </div>
        </div>
      </div>
    </div>
  );
}
